import { Fragment } from "react";
import { Check, ChevronRight, Clock, X, XCircle } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import type { OrderStatus } from "../../../../shared/const";

type Loc = { "zh-TW": string; "en-US": string; "zh-CN": string };

/** 訂單生命週期主幹 6 階段（happy path） */
const STAGES: { label: Loc }[] = [
  { label: { "zh-TW": "需求建立", "en-US": "Created", "zh-CN": "需求建立" } },
  { label: { "zh-TW": "報價", "en-US": "Quote", "zh-CN": "报价" } },
  { label: { "zh-TW": "確認成交", "en-US": "Confirmed", "zh-CN": "确认成交" } },
  { label: { "zh-TW": "指派車隊", "en-US": "Dispatch", "zh-CN": "指派车队" } },
  { label: { "zh-TW": "服務中", "en-US": "In service", "zh-CN": "服务中" } },
  { label: { "zh-TW": "完成", "en-US": "Completed", "zh-CN": "完成" } },
];

/** 每個 status 目前落在哪個階段（cancelled = 終止，不在主幹上） */
const STATUS_STAGE: Record<OrderStatus, number> = {
  pending: 0,
  quoting: 1,
  quoted: 1,
  rejected_quote: 1,
  confirmed: 2,
  dispatching: 3,
  rejected_dispatch: 3,
  active: 4,
  completed: 5,
  cancelled: -1,
};

const STATUS_HINT: Record<OrderStatus, Loc> = {
  pending: { "zh-TW": "等待調度員報價", "en-US": "Awaiting dispatcher quote", "zh-CN": "等待调度员报价" },
  quoting: { "zh-TW": "調度員報價中", "en-US": "Dispatcher is quoting", "zh-CN": "调度员报价中" },
  quoted: { "zh-TW": "報價已送達，待需求方確認", "en-US": "Quote sent — awaiting agency", "zh-CN": "报价已送达，待需求方确认" },
  rejected_quote: { "zh-TW": "報價被退回，待重新報價", "en-US": "Quote returned for revision", "zh-CN": "报价被退回，待重新报价" },
  confirmed: { "zh-TW": "已成交，待調度員指派車隊", "en-US": "Confirmed — awaiting dispatch", "zh-CN": "已成交，待调度员指派车队" },
  dispatching: { "zh-TW": "已指派車隊，待車隊接單", "en-US": "Dispatched — awaiting fleet", "zh-CN": "已指派车队，待车队接单" },
  rejected_dispatch: { "zh-TW": "車隊婉拒，待重新指派", "en-US": "Fleet declined — re-dispatch needed", "zh-CN": "车队婉拒，待重新指派" },
  active: { "zh-TW": "車隊已接單，服務進行中", "en-US": "Fleet accepted — service active", "zh-CN": "车队已接单，服务进行中" },
  completed: { "zh-TW": "服務已完成", "en-US": "Service completed", "zh-CN": "服务已完成" },
  cancelled: { "zh-TW": "此訂單已取消", "en-US": "This order was cancelled", "zh-CN": "此订单已取消" },
};

export function OrderStatusTracker({
  status,
  pickupTime,
}: {
  status: OrderStatus;
  pickupTime: number;
}) {
  const { t } = useLanguage();

  // ── 已取消：終止狀態，不走主幹 ───────────────────────────
  if (status === "cancelled") {
    return (
      <Card className="flex items-center gap-2.5 border-destructive/30 bg-destructive-soft/40 p-4">
        <XCircle className="h-5 w-5 shrink-0 text-destructive" />
        <p className="text-[13px] font-medium text-destructive">{t(STATUS_HINT.cancelled)}</p>
      </Card>
    );
  }

  const current = STATUS_STAGE[status];
  const isComplete = status === "completed";
  const isRejected = status === "rejected_quote" || status === "rejected_dispatch";

  return (
    <Card className="p-5">
      {/* header：目前階段 + 距出發倒數 */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-0.5">
          <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-muted-foreground">
            {t({ "zh-TW": "訂單進度", "en-US": "Order progress", "zh-CN": "订单进度" })}
          </p>
          <p className="font-display text-[17px] font-medium tracking-tight">
            {t(STAGES[isComplete ? 5 : current]!.label)}
          </p>
          <p className="text-[12px] text-muted-foreground">{t(STATUS_HINT[status])}</p>
        </div>
        <Countdown pickupTime={pickupTime} hidden={isComplete} />
      </div>

      {/* stepper */}
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-[440px] items-start">
          {STAGES.map((stage, i) => {
            const nodeState =
              isComplete || i < current
                ? "done"
                : i === current
                ? isRejected
                  ? "rejected"
                  : "current"
                : "upcoming";
            const connState =
              isComplete || i < current
                ? "done"
                : i === current
                ? isRejected
                  ? "blocked"
                  : "active"
                : "upcoming";
            return (
              <Fragment key={i}>
                <div className="flex w-16 shrink-0 flex-col items-center gap-2">
                  <StageDot state={nodeState} index={i} />
                  <span
                    className={cn(
                      "text-center text-[12px] leading-tight transition-colors",
                      nodeState === "current" && "font-semibold text-primary",
                      nodeState === "rejected" && "font-semibold text-destructive",
                      nodeState === "done" && "text-foreground/70",
                      nodeState === "upcoming" && "text-muted-foreground/60",
                    )}
                  >
                    {t(stage.label)}
                  </span>
                </div>
                {i < STAGES.length - 1 && (
                  <div className="flex h-7 flex-1 items-center px-0.5">
                    <Connector state={connState} />
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ── 階段節點 ─────────────────────────────────────────────── */
function StageDot({
  state,
  index,
}: {
  state: "done" | "current" | "rejected" | "upcoming";
  index: number;
}) {
  return (
    <div className="relative flex h-7 w-7 items-center justify-center">
      {state === "current" && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40" />
      )}
      <span
        className={cn(
          "relative flex h-7 w-7 items-center justify-center rounded-full border text-[12px] font-semibold tabular-nums",
          state === "done" && "border-primary bg-primary text-primary-foreground",
          state === "current" &&
            "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_color-mix(in_oklab,var(--color-primary)_16%,transparent)]",
          state === "rejected" && "border-destructive bg-destructive text-destructive-foreground",
          state === "upcoming" && "border-border bg-card text-muted-foreground",
        )}
      >
        {state === "done" ? (
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        ) : state === "rejected" ? (
          <X className="h-3.5 w-3.5" strokeWidth={3} />
        ) : (
          index + 1
        )}
      </span>
    </div>
  );
}

/* ── 階段連接線 ───────────────────────────────────────────── */
function Connector({ state }: { state: "done" | "active" | "blocked" | "upcoming" }) {
  if (state === "active") {
    // 動態箭頭：往下一階段流動
    return (
      <div className="relative h-4 w-full">
        <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-[color-mix(in_oklab,var(--color-primary)_22%,var(--color-border))]" />
        <div className="absolute inset-0 flex items-center justify-center gap-[3px]">
          {[0, 1, 2].map((k) => (
            <ChevronRight
              key={k}
              className="h-3.5 w-3.5 text-primary"
              strokeWidth={2.75}
              style={{ animation: `arrowFlow 1.5s ease-in-out ${k * 0.18}s infinite` }}
            />
          ))}
        </div>
      </div>
    );
  }
  if (state === "done") {
    return <div className="h-[2px] w-full rounded-full bg-primary" />;
  }
  if (state === "blocked") {
    return <div className="h-[2px] w-full rounded-full bg-destructive/45" />;
  }
  return <div className="h-0 w-full border-t-2 border-dotted border-border" />;
}

/* ── 距出發倒數 ───────────────────────────────────────────── */
function Countdown({ pickupTime, hidden }: { pickupTime: number; hidden: boolean }) {
  const { t } = useLanguage();
  if (hidden) return null;

  const diff = pickupTime - Date.now();

  if (diff <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-warm/40 bg-warm-soft px-2.5 py-1 text-[12px] font-medium text-warm-soft-foreground">
        <Clock className="h-3.5 w-3.5" />
        {t({ "zh-TW": "出發時間已到", "en-US": "Departure reached", "zh-CN": "出发时间已到" })}
      </span>
    );
  }

  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const d = t({ "zh-TW": "天", "en-US": "d", "zh-CN": "天" });
  const h = t({ "zh-TW": "小時", "en-US": "h", "zh-CN": "小时" });
  const m = t({ "zh-TW": "分", "en-US": "m", "zh-CN": "分" });
  const body =
    days > 0 ? `${days}${d} ${hours}${h}` : hours > 0 ? `${hours}${h} ${mins}${m}` : `${mins}${m}`;
  const urgent = diff < 86_400_000; // 不到一天

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium tabular-nums",
        urgent
          ? "border-warm/40 bg-warm-soft text-warm-soft-foreground"
          : "border-primary/25 bg-primary-soft/60 text-primary-soft-foreground",
      )}
    >
      <Clock className="h-3.5 w-3.5" />
      {t({ "zh-TW": "距出發", "en-US": "Departs in", "zh-CN": "距出发" })} {body}
    </span>
  );
}
